import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_GZ_MEGA_MENU } from '../../../webcomponents.constants';
import { MegaMenuEvents, MegaMenuIconMap, MegaMenuItem, MegaMenuSection } from '../types';
import { GzInputSearchWebComponent, InputWidthValues } from '../../gzInput';
import { classMap } from 'lit/directives/class-map.js';
import { debounce } from 'lodash';
import { FontSize } from '../../gzIconButton/types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { ifDefined } from 'lit/directives/if-defined.js';

@customElement(WEB_COMPONENT_GZ_MEGA_MENU)
export class GzMegaMenuWebComponent extends LitElement {
  @property({ type: Object, attribute: true, reflect: true })
  sections: MegaMenuSection[];

  @property({ type: Object, attribute: true, reflect: true })
  iconMap: MegaMenuIconMap;

  @property({ type: String, attribute: true, reflect: true })
  searchPlaceholder = '';

  @property({ type: String, attribute: true, reflect: true })
  noResultsMessage = '';

  @property({ type: String, attribute: true, reflect: true })
  quickActionsString = '';

  /**
   * If true, the sections will be displayed in the order they are provided in the `sections` property.
   * If false, the sections will be displayed in descending order, by the number of items in each section.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  keepInitialOrder = false;

  @query('gz-input-search')
  private inputSearch: GzInputSearchWebComponent;

  @query('.scrollable-container')
  private scrollableContainer: HTMLElement;

  // there will be 2 actions rendered per quick action section
  // there shouldn't be more quick action sections than actual sections (given by this.sections.length)
  private actionsPerQuickActionSection = 2;

  static get styles() {
    return styles;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.inputSearch) {
      this.inputSearch.inputFocus();
    }
  }

  private getQuickActions(): MegaMenuItem[] {
    if (!this.sections?.length) {
      return [];
    }
    return this.sections
      .map(section => section.items.filter(item => item.isQuickAction))
      .flat()
      .filter(item => this.matchesSearchCriteria(item));
  }

  private renderSections(): TemplateResult | undefined {
    if (!this.sections?.length) {
      return;
    }
    // display first the sections which include items which match the search criteria
    // then only empty places for the sections who do not have items matching the search criteria
    // all while keeping the original indices
    let sections = this.sections.map((section, index) => ({
      ...section,
      index,
      items: section.items.filter(item => this.matchesSearchCriteria(item)),
    }));

    if (!this.keepInitialOrder) {
      sections = sections.sort((a, b) => b.items.length - a.items.length);
    }

    // display no results message if there are no displayed items at all (first section contains no displayed items)
    const displayNoResultsMessage = sections[0].items.length === 0;

    return html`
      <div class="section-headers">${sections.map(section => this.renderSectionHeader(section))}</div>
      <div class="sections">
        ${sections.map((section, index) => this.renderSection(section, !sections[index + 1]?.items.length))}
        ${this.renderNoResultsMessage(displayNoResultsMessage)}
      </div>
    `;
  }

  private isSectionDisabled(section: MegaMenuSection): boolean {
    return section.items.every(item => item.disabled);
  }

  private renderSectionHeader(section: MegaMenuSection): TemplateResult {
    const classes = {
      disabled: this.isSectionDisabled(section),
    };
    const filteredItems = section.items.filter(item => this.matchesSearchCriteria(item));
    if (filteredItems.length === 0) {
      return html`<div class="section-header"></div>`;
    }
    const iconColor = this.isSectionDisabled(section) ? 'var(--grey-layout-200-disabled)' : 'secondary';
    return html`
      <div class="section-header ${classMap(classes)}">
        <gz-icon size="24" color="${iconColor}"> ${this.iconMap[section.icon]} </gz-icon>
        <span>${section.label}</span>
      </div>
    `;
  }

  private renderSection(section: MegaMenuSection, isLastSection: boolean): TemplateResult | undefined {
    const filteredItems = section.items.filter(item => this.matchesSearchCriteria(item));
    if (filteredItems.length === 0) {
      return html`<div class="section"></div>`;
    }
    const classes = { section: true, border: !isLastSection };
    return html` <div class="${classMap(classes)}">${filteredItems.map(item => this.renderSectionItem(item))}</div> `;
  }

  private renderQuickActions(): TemplateResult | undefined {
    const quickActions = this.getQuickActions();
    if (!this.sections?.length || !quickActions?.length) {
      return;
    }
    return html`
      <div class="quick-actions">
        <gz-text fontSize="${FontSize.ICON_SIZE_12}" class="text-semibold text-uppercase quick-actions-header">
          ${this.quickActionsString}
        </gz-text>
        <div class="sections">
          ${this.sections.map((_, sectionIndex) => this.renderQuickActionsSection(quickActions, sectionIndex))}
        </div>
      </div>
    `;
  }

  private renderQuickActionsSection(quickActions: MegaMenuItem[], sectionIndex: number): TemplateResult {
    const dummyArray = Array.from({ length: this.actionsPerQuickActionSection });
    return html`
      <div class="section">
        ${dummyArray.map((_, index) =>
          this.renderSectionItem(quickActions?.[sectionIndex * this.actionsPerQuickActionSection + index], true)
        )}
      </div>
    `;
  }
  private renderSectionItem(item: MegaMenuItem | undefined, isQuickAction = false): TemplateResult | undefined {
    if (!item) {
      return;
    }
    const showDisabledMessage = item.disabled && item.disabledMessage;
    const tooltipText = showDisabledMessage ? item.disabledMessage : item.label;

    return html`
      <button
        id="${ifDefined(item.id)}"
        class="item"
        ?disabled="${item.disabled}"
        @click="${() => this.onItemClick(item)}"
      >
        <gz-tooltip
          ?isEllipsis="${!showDisabledMessage}"
          tooltipText="${tooltipText}"
          class="${classMap({
            'has-icon': !isEmpty(item.icon) && !isQuickAction,
          })}"
        >
          ${this.highlightSearchedString(item.label)} ${this.renderItemIcon(item, isQuickAction)}
        </gz-tooltip>
      </button>
    `;
  }

  private renderItemIcon(item: MegaMenuItem, isQuickAction = false): TemplateResult | undefined {
    if (!item.icon || isQuickAction) {
      return;
    }

    const iconColor = item.disabled ? 'var(--grey-layout-200-disabled)' : 'secondary';

    return html`<gz-icon size="14" .color="${iconColor}" class="item-icon"> ${this.iconMap[item.icon]} </gz-icon>`;
  }

  private renderNoResultsMessage(shouldRender: boolean): TemplateResult | undefined {
    if (!shouldRender) {
      return;
    }
    return html`<div class="no-results">${this.noResultsMessage}</div>`;
  }

  private highlightSearchedString(label: string): TemplateResult {
    const inputSearchValue = this.getInputSearchValue();
    if (inputSearchValue === '') {
      return html`${label}`;
    }
    const [beforeMatch, match, afterMatch] = this.splitWithMatch(label, inputSearchValue);
    return html`${beforeMatch}<b>${match}</b>${afterMatch}`;
  }

  private splitWithMatch(inputString: string, searchString: string): [string, string, string] {
    const index = inputString.toLowerCase().indexOf(searchString.toLowerCase());
    if (index !== -1) {
      const beforeMatch = inputString.substring(0, index);
      const match = inputString.substring(index, index + searchString.length);
      const afterMatch = inputString.substring(index + searchString.length);
      return [beforeMatch, match, afterMatch];
    }
    return ['', '', ''];
  }

  private onItemClick(item: MegaMenuItem): void {
    if (item.disabled) {
      return;
    }
    this.dispatchEvent(
      new CustomEvent(MegaMenuEvents.ITEM_CLICKED, { detail: item.event, bubbles: true, composed: true })
    );
    item.action?.();
  }

  private handleSearchInputChange(): void {
    this.requestUpdate();
    this.scrollableContainer.scrollTop = 0;
    this.scrollableContainer.scrollLeft = 0;
  }

  private matchesSearchCriteria(item: MegaMenuItem): boolean {
    const inputSearchValue = this.getInputSearchValue().toLowerCase();
    if (inputSearchValue === '') {
      return true;
    }
    return item.label.toLowerCase().includes(inputSearchValue);
  }

  private getTotalItemsCount(): number {
    return this.sections?.reduce((acc, section) => acc + section.items.length, 0) ?? 0;
  }

  private getInputSearchValue(): string {
    if (!this.inputSearch) {
      return '';
    }
    return this.inputSearch.value;
  }

  private allSectionsDisabled(): boolean {
    return this.sections?.every(section => section.items.every(item => item.disabled)) ?? false;
  }

  render(): TemplateResult {
    return html`
      <div class="wrapper fs-12">
        ${this.getTotalItemsCount() > 9
          ? html`
              <div class="search-container">
                <gz-input-search
                  ?disabled="${this.allSectionsDisabled()}"
                  inputWidth="${InputWidthValues.XLARGE}"
                  placeholder="${this.searchPlaceholder}"
                  @change="${debounce(this.handleSearchInputChange, 200)}"
                />
              </div>
            `
          : nothing}
        <div
          class="scrollable-container ${classMap({
            'has-search-bar': this.getTotalItemsCount() > 9,
          })}"
        >
          ${this.renderQuickActions()} ${this.renderSections()}
        </div>
      </div>
    `;
  }

  public resetInputSearch(): void {
    this.inputSearch.clearInputValue(new Event('clear'));
  }
}
